import Modal from '@/components/Dashboard/Modal';
import modalStyles from '@/components/Dashboard/Modal.module.scss';
import "@common/assets/styles/theme.css";
import {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    Outlet,
    useLocation,
    useSearchParams,
    useNavigate
} from 'react-router-dom';

import { OnboardingSuccessModal } from '@/components/Onboarding/OnboardingSuccess';
import { useAdminRuntimeBackfill } from '@/components/Onboarding/useAdminRuntimeBackfill';
import { useAdminRuntimeLaunch } from '@/components/Onboarding/useAdminRuntimeLaunch';
import {
    readIntegrationRouteIntent,
    writeIntegrationRouteIntent,
} from '@/components/Settings/integrationRouteIntent';
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from '@client-app/i18n';
import { featureService } from '@common/api/featureService';
import styles from "./Admin.module.scss";
import { ChatSidebar } from "./components/chat/ChatSidebar";
import DebugFloatWindow from "./components/DebugFloatWindow";
import { Sidebar } from "./components/Menu";
import { useAdminContext } from "./contexts/AdminContext";
import { shouldWaitForOnboardingSuccessWorkspace } from './adminOnboardingSuccessFlow';
import { getRouteBehavior, isAdminLayoutGlobalLoadingOwner } from './routeBehavior';

function AdminLayout() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const {
        currentWorkspace, isLoadingProfile, isLoadingWorkspaces, showGlobalLoading, hideGlobalLoading
    } = useAuth();
    const { chat, dashboard } = useAdminContext();
    const {isChatVisible, setIsChatVisible} = chat;
    const {
        isEditMode, setIsEditMode, editorStates, selectedVersion, resetEditor,
        versions, setSelectedVersion
    } = dashboard;
    const didAutoOpenDashboardChatRef = useRef(false);
    const [isExitModalOpen, setIsExitModalOpen] = useState(false);
    const prevIsEditMode = useRef(isEditMode);
    const runtimeLaunch = useAdminRuntimeLaunch();
    const routeBehavior = useMemo(
        () => getRouteBehavior(location.pathname),
        [location.pathname],
    );
    const adminLayoutOwnsGlobalLoading = useMemo(
        () => isAdminLayoutGlobalLoadingOwner(location.pathname),
        [location.pathname],
    );
    const isLaunchHydrating = searchParams.get('launchHydrating') === '1';
    const hasOnboardingSuccessParam = searchParams.get('onboarding_success') === 'true';
    const requestedOnboardingWorkspaceId = searchParams.get('workspace_id')?.trim()
        || searchParams.get('workspaceId')?.trim()
        || '';
    const isWaitingForOnboardingWorkspaceReturn = shouldWaitForOnboardingSuccessWorkspace({
        hasSuccessParam: hasOnboardingSuccessParam,
        requestedWorkspaceId: requestedOnboardingWorkspaceId,
        currentWorkspaceId: currentWorkspace?.id,
    });
    useAdminRuntimeBackfill({
        workspaceId: currentWorkspace?.id,
        disabled: runtimeLaunch.handlingReturn || runtimeLaunch.active,
        launchHydrating: isLaunchHydrating,
        loading: isLoadingProfile || isLoadingWorkspaces,
    });

    // 监听 onboarding 成功参数（使用 state 保持显示状态）
    const [showCelebration, setShowCelebration] = useState(false);
    const [celebrationRunId, setCelebrationRunId] = useState(0);

    // 统一的 loading 状态
    const shouldBlockForDashboardLoad = location.pathname === '/' || location.pathname === '/home';
    const isLoading = useMemo(
        () => isLoadingProfile
            || isLoadingWorkspaces
            || isWaitingForOnboardingWorkspaceReturn
            || (shouldBlockForDashboardLoad && dashboard.isLoadingVersions),
        [dashboard.isLoadingVersions, isLoadingProfile, isLoadingWorkspaces, isWaitingForOnboardingWorkspaceReturn, shouldBlockForDashboardLoad]
    );
    // 使用 AuthContext 控制 loading
    useEffect(() => {
        if (!adminLayoutOwnsGlobalLoading) {
            hideGlobalLoading();
            return;
        }
        if (runtimeLaunch.handlingReturn || runtimeLaunch.active || isLaunchHydrating) {
            hideGlobalLoading();
            return;
        }
        if (isLoading) {
            showGlobalLoading();
        } else {
            hideGlobalLoading();
        }
    }, [adminLayoutOwnsGlobalLoading, hideGlobalLoading, isLaunchHydrating, isLoading, runtimeLaunch.active, runtimeLaunch.handlingReturn, showGlobalLoading]);

    useEffect(() => {
        if (isLoadingProfile || !currentWorkspace?.id) {
            return;
        }

        void featureService.prefetchIntegrationPermissions().catch(() => {});
    }, [currentWorkspace?.id, isLoadingProfile]);

    useEffect(() => {
        if (!isLaunchHydrating || isLoading) {
            return;
        }

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('launchHydrating');
        const nextSearch = nextSearchParams.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
        window.history.replaceState({}, '', nextUrl);
    }, [isLaunchHydrating, isLoading, searchParams]);

    // Redirect setting_modal query parameter to standalone route
    useEffect(() => {
        const settingModal = searchParams.get('setting_modal');
        const integrationRouteIntent = readIntegrationRouteIntent(searchParams);
        const workspaceId = searchParams.get('workspace_id') || searchParams.get('workspaceId');
        
        if (settingModal) {
            let route = 'profile';
            if (settingModal === 'workspace-settings' || settingModal === 'brand') {
                route = 'workspace-settings';
            } else if (settingModal === 'integration' || settingModal === 'integrations') {
                route = 'integrations';
            } else if (settingModal === 'members') {
                route = 'members';
            } else if (settingModal === 'billing') {
                route = 'billing';
            } else if (settingModal === 'usage') {
                route = 'usage';
            }
            
            const nextParams = new URLSearchParams();
            writeIntegrationRouteIntent(nextParams, integrationRouteIntent);
            if (workspaceId) {
                nextParams.set('workspace_id', workspaceId);
            }
            
            const searchStr = nextParams.toString();
            navigate(`/${route}${searchStr ? `?${searchStr}` : ''}`, { replace: true });
        }
    }, [searchParams, navigate]);

    const shouldShowCelebration = showCelebration && !isLoading && !runtimeLaunch.handlingReturn;

    useEffect(() => {
        if (isWaitingForOnboardingWorkspaceReturn || isLoading || runtimeLaunch.handlingReturn) {
            return;
        }

        if (!hasOnboardingSuccessParam) {
            return;
        }

        setShowCelebration(true);

        if (hasOnboardingSuccessParam) {

            // Let workspace return handling consume workspace_id first.
            // Clearing onboarding_success too early makes the first admin refresh
            // lose its "successful return" context and can bounce back to onboarding.
            if (requestedOnboardingWorkspaceId) {
                return;
            }

            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.delete('onboarding_success');
            nextSearchParams.delete('planCode');
            nextSearchParams.delete('planName');
            const nextSearch = nextSearchParams.toString();
            const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
            window.history.replaceState({}, '', nextUrl);
        }

        const celebrationTimer = window.setTimeout(() => {
            setShowCelebration(false);
        }, 3000);

        return () => {
            window.clearTimeout(celebrationTimer);
        };
    }, [hasOnboardingSuccessParam, isLoading, isWaitingForOnboardingWorkspaceReturn, requestedOnboardingWorkspaceId, runtimeLaunch.handlingReturn, searchParams]);
    
    // Check dirty state
    const dashboardId = selectedVersion?.id || 'temp';
    const isDirty = editorStates[dashboardId]?.isDirty || false;

    useEffect(() => {
        // Detect switch from Edit Mode ON -> OFF
        if (prevIsEditMode.current && !isEditMode) {
            setIsChatVisible(false);
        }
        prevIsEditMode.current = isEditMode;

        if (isEditMode) {
            setIsChatVisible(true);
            return;
        }

        if (location.pathname !== '/') {
            didAutoOpenDashboardChatRef.current = false;
        }
    }, [isEditMode, location.pathname, setIsChatVisible]);
    
    // Determine chat mode based on route
    // Only use 'dashboard' mode when strictly on root path
    const chatMode = location.pathname === '/' ? 'dashboard' : 'chat';

    const handleBackClick = () => {
        if (isDirty) {
            setIsExitModalOpen(true);
        } else {
            const isPersisted = versions.some(v => v.id === dashboardId);
            setIsEditMode(false);

            if (!isPersisted) {
                resetEditor(dashboardId);
                if (versions.length > 0) {
                    setSelectedVersion(versions[0]);
                } else {
                    setSelectedVersion(null);
                }
            }
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.layout}>
                {!isEditMode && (
                    <Sidebar
                        collapseOnNarrowViewport={routeBehavior.collapseSidebarOnNarrowViewport}
                        forceCollapsed={isEditMode || location.pathname.startsWith('/pulse/')}
                    />
                )}
                <div className={styles.main}>
                    {/* {isEditMode ? (
                        <div className={styles.editHeader}>
                            <button
                                onClick={handleBackClick}
                                className={styles.backButton}
                                title="Back"
                            >
                                <ArrowLeft size={14} />
                                <span>Back</span>
                            </button>
                            <div id="admin-edit-header-content" style={{ flex: 1 }}></div>
                        </div>
                    ) : (
                        <div className={styles.nav}>
                            <div></div>
                            <div className={styles.navRight}>
                                {!isChatVisible && (
                                <button 
                                    className={styles.bustlyAiBtn}
                                    onClick={() => setIsChatVisible(true)}
                                >
                                    <img src={bustlyAiIcon} alt="Bustly AI" />
                                    Bustly AI
                                </button>
                                )}
                            </div>
                        </div>
                    )} */}
                    <div className={styles.content}>
                        <Content />
                    </div>
                </div>
                <div id="chat_bar" className={`${styles.chat} ${isChatVisible ? styles.visible : ''}`}>
                    <ChatSidebar 
                        className={styles.chatSidebar} 
                        onClose={() => setIsChatVisible(false)}
                        mode={chatMode}
                        isEditMode={isEditMode}
                        onToggleEditMode={() => setIsEditMode(prev => !prev)}
                    />
                </div>
                {/* <ChatToggle isChatVisible={isChatVisible} setIsChatVisible={setIsChatVisible} /> */}
                {import.meta.env.VITE_DEBUG === 'true' && <DebugFloatWindow />}
            
                <Modal
                    isOpen={isExitModalOpen}
                    onClose={() => setIsExitModalOpen(false)}
                    title={t('website.admin.unsavedChangesTitle')}
                    width="max-w-sm"
                >
                    <div className={modalStyles.modalBody}>
                        <p className={modalStyles.modalText}>
                            {t('website.admin.unsavedChangesDescription')}
                        </p>
                        <div className={modalStyles.modalFooter}>
                            <button
                                onClick={() => setIsExitModalOpen(false)}
                                className={modalStyles.modalCancelBtn}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={() => {
                                    setIsExitModalOpen(false);
                                    setIsEditMode(false);
                                    resetEditor(dashboardId);

                                    const isPersisted = versions.some(v => v.id === dashboardId);
                                    if (!isPersisted) {
                                        if (versions.length > 0) {
                                            setSelectedVersion(versions[0]);
                                        } else {
                                            setSelectedVersion(null);
                                        }
                                    }
                                }}
                                className={modalStyles.modalDeleteBtn}
                                style={{ backgroundColor: '#dc2626' }}
                            >
                                {t('website.admin.discardAndLeave')}
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Onboarding Success Modal */}
                <OnboardingSuccessModal
                    key={celebrationRunId}
                    isOpen={shouldShowCelebration}
                    onClose={() => {
                        setShowCelebration(false);
                    }}
                />
            </div>
        </div>
    );
}

const Content = () => {
    return (
        <Outlet />
    )
}

export default AdminLayout
